import { useCallback, useEffect, useRef, useState, } from 'react';
import { batch, useDispatch } from 'react-redux';
import { partial, removeNulls } from 'utils';
import { debounce } from 'debounce';
/**
 * @function
 * Нужна для простоты использования всплывающих меню
 * @return [
 * ссылка на дом элемент, где кликали;
 * ссылка на DOM-элемент, где надо открыться;
 * открыть меню;
 * закрыть меню;
 * ]
 */
export function useRefAnchor() {
    const ref = useRef(null);
    const [anchor, setAnchor] = useState(null);
    const open = useCallback(() => setAnchor(ref.current), []);
    const close = useCallback(() => setAnchor(null), []);
    return [anchor, ref, open, close];
}
/**
 * @function
 * Нужна для простоты использования всплывающих меню
 * @return [ссылка на дом элемент, открыть меню, закрыть меню]
 */
export function useAnchor() {
    const [anchor, setAnchor] = useState(null);
    const open = useCallback((event) => {
        if (event.type === 'touchstart') {
            preventDefault(event);
        }
        setAnchor(event.currentTarget);
    }, [setAnchor]);
    const close = useCallback(() => setAnchor(null), [setAnchor]);
    return [anchor, open, close];
}
/**
 * @function
 * @param callback: функция, которая выполниться позже
 * @return мемоизированная функция, перед которой выполниться
 * preventDefault & stopPropagation
 */
export function usePreventDefault(callback) {
    return useCallback((event) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        callback();
    }, [callback]);
}
export function preventDefault(event) {
    event.preventDefault();
    event.stopPropagation();
}
export function stopPropagation(event) {
    event.stopPropagation();
}
/**
 * @function
 * @param f: функция
 * @param p1: первый аргумент функции
 * @return мемоизированная функция, которая в момент вызова будет вести себя так,
 * как f(p1, ...args)
 * usePartial(f, ...p1)(...p2) --> f(...p1, ...p2)
 */
export function usePartial(f, ...p1) {
    return useCallback(partial(f, ...p1), [f, ...p1]);
}
/**
 * @function
 * @param functions: функции
 * @return функция, которая запустит все функции,
 * переданные в параметры
 */
export function callbacks(functions) {
    return (...args) => functions.map((action) => action(...args));
}
/**
 * @function
 * @param functions: функции
 * @return мемоизированная функция, которая запустит все функции,
 * переданные в параметры
 */
export function useCallbacks(functions) {
    return useCallback(callbacks(functions), functions);
}
export function useNullableCallbacks(functions) {
    return useCallback((...args) => removeNulls(functions.map((action) => (action ? action?.(...args) : null))), functions);
}
/**
 * @function
 * @param functions: функции
 * @return мемоизированная функция, которая запустит все redux actions,
 * переданные в параметры
 */
export function useBatchActions(functions) {
    const dispatch = useDispatch();
    return useCallback((...args) => {
        batch(() => functions.map((action) => dispatch(action(...args))));
    }, functions);
}
/**
 * Создаёт мутабельный ref-объект, со значением value.
 * Значение поддерживается актуальным с помощью useEffect.
 * Это может быть полезно в таком сценарии:
 *
 * function Adder({value, onSet}) {
 *     const valueBox = useBox(value);
 *
 *     const onClick = useCallback(() => {
 *         onSet(valueBox + 1);
 *     }, [onSet]);
 *     // ↑ обратите внимание, что в зависимостях только onSet, но не value и не valueBox
 *     // Это позволяет дочернему компоненту не ререндериться при изменении value, ведь
 *     // коллбэк onClick не пересоздаётся.
 *
 *     return <button onClick={onClick}>add one</button>;
 * }
 *
 * const [value, setValue] = useState(0);
 *
 * <Adder value={value} onSet={setValue}/>
 *
 * @param value  значение, которое нужно положить в ref
 * @returns {any}  текущее значение ref'а
 */
export function useBox(value) {
    const ref = useRef(value);
    ref.current = value;
    return ref;
}
export function useMounted() {
    const isMounted = useRef(true);
    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);
    return isMounted;
}
export const useSafeEffect = (effect, deps) => {
    const isMounted = useMounted();
    useEffect(() => {
        let clean_up;
        if (isMounted.current) {
            clean_up = effect();
        }
        return () => {
            //@ts-ignore
            clean_up();
        };
    }, [deps]);
};
export const useInView = (offset = 0, throttleMilliseconds = 300) => {
    const [inView, setIsView] = useState(false);
    const ref = useRef(null);
    const onScroll = debounce(() => {
        if (!ref.current) {
            setIsView(false);
            return;
        }
        const position = ref.current.getBoundingClientRect().top > 0
            ? ref.current.getBoundingClientRect().top
            : ref.current.getBoundingClientRect().bottom;
        setIsView(position + offset >= 0 && position - offset <= window.innerHeight);
    }, throttleMilliseconds);
    useEffect(() => {
        onScroll();
        document.addEventListener('scroll', onScroll, true);
        return () => document.removeEventListener('scroll', onScroll, true);
    }, []);
    return [inView, ref];
};
export function usePromiseResult(promise, clear_on_promise_change) {
    const [result, setResult] = useState(null);
    const isMounted = useMounted();
    useEffect(() => {
        if (isMounted.current) {
            if (clear_on_promise_change) {
                setResult(null);
            }
            promise.then((result) => {
                if (isMounted.current) {
                    setResult({ result });
                }
            }, (error) => {
                if (isMounted.current) {
                    setResult({ error });
                }
            });
        }
    }, [promise]);
    return result;
}
